/**
 * The batch envelope every `/bulk/*` route in the gateway answers with, and the two
 * rules that come with it.
 *
 * **A 200 does not mean everything worked.** The routes deliberately answer 200 with a
 * per-item outcome rather than failing the batch on the first bad id: `succeeded` lists
 * what applied, `failed` names what did not and why. Any screen that fires a batch and
 * then says «Готово» on a resolved promise is lying whenever `failed` is non-empty —
 * `describeOutcome` below is the honest reading.
 *
 * **At most 500 ids per call**, counted before duplicates are removed; more is a 400
 * and NOTHING is applied. `chunk()` is how a caller with a bigger selection splits it,
 * and `mergeOutcomes()` folds the answers back into one.
 */

/** One id that did not apply, and why. */
export type BulkFailure = {
  /** The identifier that failed, exactly as sent. */
  id: string;
  /** Per-item HTTP-shaped status: 404 unknown, 409 ambiguous, 403 out of scope. */
  code: number;
  /** Human-readable reason, from the server. */
  error: string;
};

export type BulkOutcome = {
  /** Identifiers considered, after duplicates were removed. */
  total: number;
  succeeded: string[];
  failed: BulkFailure[];
};

/** The server's cap on one call. */
export const BULK_LIMIT = 500;

/** Split a selection into calls the server will accept. */
export function chunk<T>(items: T[], size: number = BULK_LIMIT): T[][] {
  if (items.length <= size) {
    return [items];
  }
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
}

/** Fold several chunked outcomes into the one the user is shown. */
export function mergeOutcomes(parts: BulkOutcome[]): BulkOutcome {
  return parts.reduce<BulkOutcome>(
    (acc, part) => ({
      total: acc.total + (part?.total ?? 0),
      succeeded: [...acc.succeeded, ...(part?.succeeded ?? [])],
      failed: [...acc.failed, ...(part?.failed ?? [])],
    }),
    {total: 0, succeeded: [], failed: []},
  );
}

/**
 * Run a batch operation over any number of ids, chunked to the server's cap.
 * Sequential, not parallel: five hundred devices per call already saturates the
 * gateway's own fan-out, and firing four such calls at once is how a bulk action
 * becomes an outage.
 */
export async function runBulk(
  ids: string[],
  call: (batch: string[]) => Promise<BulkOutcome>,
): Promise<BulkOutcome> {
  const parts: BulkOutcome[] = [];
  for (const batch of chunk(ids)) {
    parts.push(await call(batch));
  }
  return mergeOutcomes(parts);
}
